/**
 * Linear-interpolation downsampler from ANY AudioContext rate to 16 kHz, plus the framer that
 * turns 16 kHz float output into fixed 100 ms Int16 frames.
 *
 *  - keeps the fractional read position and the last input sample across chunks, so 128-frame
 *    render quanta (or 4096-frame ScriptProcessor buffers) join with no clicks and the long-run
 *    output rate is exactly outRate
 *  - optional one-pole low-pass pre-filter (cutoff 0.45 * outRate) to tame aliasing; adequate for STT
 *
 * The AudioWorklet file (pcm-capture-processor.worklet.js) inlines an identical copy because worklet
 * scopes cannot import. Keep both in sync.
 */

/** Sample rate of every frame handed to the detector / segmenter / WAV encoder. */
export const TARGET_SAMPLE_RATE = 16000;
/** Frame length in milliseconds. All VAD timers are multiples of this. */
export const FRAME_MS = 100;
/** Samples per frame at TARGET_SAMPLE_RATE. */
export const FRAME_SAMPLES = (TARGET_SAMPLE_RATE * FRAME_MS) / 1000; // 1600

/** Message shape shared by the worklet tier, the ScriptProcessor tier (Int16Framer) and the consumers. */
export interface PcmFrame {
  type: "frame" | "flush";
  /** 0-based frame counter within one capture session (worklet node / framer instance). */
  seq: number;
  /** 16 kHz mono signed 16-bit samples; FRAME_SAMPLES long for 'frame', shorter for 'flush'. */
  pcm: Int16Array;
  /** dBFS of this frame computed on the float samples (digital silence reads about -180). */
  rmsDb: number;
  /** Peak absolute float sample, 0..1. */
  peak: number;
  /** True when every sample was exactly zero (dead tap), distinct from merely quiet. */
  allZero: boolean;
  /** Diagnostic: the AudioContext rate the frame was downsampled from (worklet only). */
  contextRate?: number;
}

export interface LinearDownsamplerOptions {
  /** Default true. Set false to skip the anti-alias pre-filter (tests / already band-limited input). */
  prefilter?: boolean;
}

export class LinearDownsampler {
  readonly inRate: number;
  readonly outRate: number;
  /** Input samples per output sample: 3.0 at 48 kHz, 2.75625 at 44.1 kHz. */
  readonly ratio: number;
  readonly prefilter: boolean;
  readonly alpha: number;
  /** Fractional read index into the current chunk; invariant -1 <= pos < ratio. */
  private pos = 0;
  /** Last (filtered) input sample of the previous chunk = virtual index -1. */
  private last = 0;
  /** One-pole filter state. */
  private lp = 0;
  private scratch: Float32Array | null = null;

  /**
   * @param inRate  the AudioContext sampleRate (48000 or 44100 on phones) - never a constant
   * @param outRate target rate, default 16000
   */
  constructor(inRate: number, outRate: number = TARGET_SAMPLE_RATE, opts: LinearDownsamplerOptions = {}) {
    if (!(inRate > 0) || !(outRate > 0)) throw new RangeError("LinearDownsampler: bad sample rate");
    this.inRate = inRate;
    this.outRate = outRate;
    this.ratio = inRate / outRate;
    this.prefilter = this.ratio > 1.0001 && opts.prefilter !== false;
    const fc = 0.45 * outRate;
    this.alpha = this.prefilter ? 1 - Math.exp((-2 * Math.PI * fc) / inRate) : 1;
  }

  /** Upper bound on output samples produced by an input chunk of n samples (for preallocation). */
  maxOutputLength(n: number): number {
    return Math.max(0, Math.ceil((n - 1 - this.pos) / this.ratio)) + 1;
  }

  /**
   * Consume one mono chunk entirely; write outputs into out[offset..]; return the count written.
   * Any fractional remainder is carried into the next call.
   */
  processInto(input: Float32Array, out: Float32Array, offset = 0): number {
    const n = input.length;
    if (n === 0) return 0;
    let src: Float32Array = input;
    if (this.prefilter) {
      if (!this.scratch || this.scratch.length < n) this.scratch = new Float32Array(n);
      src = this.scratch;
      let y = this.lp;
      const a = this.alpha;
      for (let i = 0; i < n; i++) {
        y += a * (input[i] - y);
        src[i] = y;
      }
      this.lp = y;
    }
    const ratio = this.ratio;
    let pos = this.pos;
    let o = offset;
    // An output at position pos interpolates src[floor(pos)] and src[floor(pos) + 1];
    // floor(pos) === -1 refers to this.last (the previous chunk's final sample).
    while (pos < n - 1) {
      const i0 = Math.floor(pos);
      const frac = pos - i0;
      const s0 = i0 < 0 ? this.last : src[i0];
      const s1 = src[i0 + 1];
      out[o++] = s0 + (s1 - s0) * frac;
      pos += ratio;
    }
    this.last = src[n - 1];
    this.pos = pos - n; // relative to the next chunk; lands in [-1, ratio - 1)
    return o - offset;
  }

  /** Allocating convenience wrapper (fine on the main thread; avoid inside a worklet process()). */
  process(input: Float32Array): Float32Array {
    const out = new Float32Array(this.maxOutputLength(input.length));
    return out.subarray(0, this.processInto(input, out, 0));
  }

  reset(): void {
    this.pos = 0;
    this.last = 0;
    this.lp = 0;
  }
}

/**
 * Accumulates 16 kHz float output into fixed 100 ms Int16 frames and emits the SAME message shape
 * as the worklet. Used by the ScriptProcessorNode tier so the detector/segmenter/encoder are shared.
 */
export class Int16Framer {
  readonly frameLen: number;
  private readonly onFrame: (m: PcmFrame) => void;
  private frame: Int16Array;
  private fill = 0;
  private seq = 0;
  private sumSq = 0;
  private peak = 0;
  private nonZero = false;

  /** @param frameLen 1600 for 100 ms at 16 kHz */
  constructor(frameLen: number, onFrame: (m: PcmFrame) => void) {
    this.frameLen = frameLen;
    this.onFrame = onFrame;
    this.frame = new Int16Array(frameLen);
  }

  /** @param f32 16 kHz float samples in [-1, 1] */
  push(f32: Float32Array): void {
    for (let i = 0; i < f32.length; i++) {
      let s = f32[i];
      if (s > 1) s = 1;
      else if (s < -1) s = -1;
      const v = Math.round(s * 32767);
      this.frame[this.fill++] = v;
      this.sumSq += s * s;
      const a = s < 0 ? -s : s;
      if (a > this.peak) this.peak = a;
      if (v !== 0) this.nonZero = true;
      if (this.fill === this.frameLen) this.emit("frame");
    }
  }

  private emit(type: PcmFrame["type"]): void {
    const n = this.fill;
    if (n === 0 && type !== "flush") return;
    const pcm = this.frame.slice(0, n);
    const rms = n ? Math.sqrt(this.sumSq / n) : 0;
    this.onFrame({
      type,
      seq: this.seq++,
      pcm,
      rmsDb: 20 * Math.log10(rms + 1e-9),
      peak: this.peak,
      allZero: !this.nonZero,
    });
    this.fill = 0;
    this.sumSq = 0;
    this.peak = 0;
    this.nonZero = false;
  }

  /** Emit whatever is buffered (possibly an empty 'flush' frame). */
  flush(): void {
    this.emit("flush");
  }
}
